'use client';

import Link from 'next/link';
import { ChevronRight } from 'lucide-react';

import type { PostAudition } from '@kit/community';
import { cdnImageUrl } from '@kit/shared/storage';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { hashSeededGradient } from '@kit/ui/entity-card';
import { Trans } from '@kit/ui/trans';

import {
  AuditionDeadline,
  auditionHref,
  AuditionTitle,
  AuditionTypeBadge,
} from './post-attachments.audition-parts';

/**
 * Floating identity pill rendered INSIDE a hero cover (top-left): small
 * avatar + name on a glassy dark chip. Kept as an absolutely-positioned
 * SIBLING of the cover link (never nested inside it — nested anchors are
 * invalid HTML).
 */
export function HeroIdentityChip({
  name,
  avatarUrl,
  href,
}: {
  name: string;
  avatarUrl: string | null;
  href?: string;
}) {
  const content = (
    <span className="flex min-w-0 items-center gap-1.5 rounded-md bg-black/55 py-1 pr-3 pl-1 backdrop-blur-sm">
      <Avatar className="h-6 w-6">
        <AvatarImage src={avatarUrl ?? undefined} />
        <AvatarFallback className="text-[10px]">
          {name[0]?.toUpperCase() ?? '?'}
        </AvatarFallback>
      </Avatar>
      <span className="truncate text-sm font-medium text-white">{name}</span>
    </span>
  );

  return (
    <span className="absolute top-3 left-3 z-10 max-w-[70%]">
      {href ? (
        <Link href={href} className="block hover:opacity-90">
          {content}
        </Link>
      ) : (
        content
      )}
    </span>
  );
}

/**
 * Hero embed — the post IS the audition, so the cover IS the card: one
 * full-bleed 16:9 visual with everything overlaid (no footer chrome).
 * Top-left: floating identity chip (passed by the post surface). Top-right:
 * deadline pill. Bottom gradient: type badge + title. The whole cover links
 * to the audition. Coverless auditions get a hash-seeded gradient.
 */
function AuditionHeroCard({
  audition,
  identity,
}: {
  audition: PostAudition;
  identity?: React.ReactNode;
}) {
  const href = auditionHref(audition);
  const initials = (audition.title ?? 'A').slice(0, 2).toUpperCase();

  return (
    // No own rounding: rendered as the card's full-bleed hero, the parent
    // Card clips the top corners and the bottom must sit FLUSH on the
    // card's action layer (rounded bottom read as a visual bug).
    <div className="relative overflow-hidden">
      {identity}
      <AuditionDeadline
        audition={audition}
        variant="overlay"
        className="absolute top-3 right-3 z-10"
      />
      <Link href={href} className="group block">
        <div
          className="relative aspect-[16/9] w-full overflow-hidden"
          style={
            audition.coverUrl
              ? undefined
              : { background: hashSeededGradient(audition.id) }
          }
        >
          {audition.coverUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={cdnImageUrl(audition.coverUrl, 'GALLERY') ?? undefined}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center text-4xl font-semibold text-white/25"
            >
              {initials}
            </span>
          )}
          {/* Bottom gradient carrying badge + title */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-4 pt-12">
            <AuditionTypeBadge audition={audition} />
            <h3 className="mt-1.5 line-clamp-2 text-lg leading-snug font-semibold text-white">
              <AuditionTitle audition={audition} />
            </h3>
          </div>
        </div>
      </Link>
    </div>
  );
}

/**
 * Chip embed — the audition referenced from a longer text post. Compact
 * horizontal card: cover thumbnail (or gradient), kicker, title, deadline.
 */
function AuditionChipCard({ audition }: { audition: PostAudition }) {
  const href = auditionHref(audition);
  const initials = (audition.title ?? 'A').slice(0, 1).toUpperCase();

  return (
    <Link
      href={href}
      className="border-border hover:bg-muted/50 group flex items-center gap-3 overflow-hidden rounded-lg border p-3 transition-colors"
    >
      <div
        className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-lg"
        style={
          audition.coverUrl
            ? undefined
            : { background: hashSeededGradient(audition.id) }
        }
      >
        {audition.coverUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cdnImageUrl(audition.coverUrl, 'THUMBNAIL') ?? undefined}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center text-xl font-semibold text-white/30"
          >
            {initials}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">
          {audition.is_competition ? (
            <Trans
              i18nKey="community:feed.linkedCompetition"
              defaults="Competition"
            />
          ) : (
            <Trans
              i18nKey="community:feed.linkedAudition"
              defaults="Audition"
            />
          )}
        </span>
        <span className="text-foreground line-clamp-1 font-medium group-hover:underline">
          <AuditionTitle audition={audition} />
        </span>
        <AuditionDeadline audition={audition} />
      </div>

      <ChevronRight
        className="text-muted-foreground h-4 w-4 shrink-0"
        aria-hidden="true"
      />
    </Link>
  );
}

export function AuditionEmbed({
  audition,
  variant,
  identity,
}: {
  audition: PostAudition;
  variant: 'hero' | 'chip';
  // Floating top-left identity chip (post author) — hero only.
  identity?: React.ReactNode;
}) {
  return variant === 'hero' ? (
    <AuditionHeroCard audition={audition} identity={identity} />
  ) : (
    <AuditionChipCard audition={audition} />
  );
}

/**
 * Threshold under which an audition post renders the hero embed: a short
 * body reads as a lead-in line for the entity; a longer one is prose that
 * merely references it. Counted on the body with bare URLs stripped so a
 * pasted link doesn't flip the variant.
 */
const HERO_BODY_MAX_CHARS = 180;
const URL_STRIP_PATTERN = /https?:\/\/\S+/g;

export function auditionEmbedVariant(body: string): 'hero' | 'chip' {
  const stripped = body.replace(URL_STRIP_PATTERN, '').trim();
  return stripped.length <= HERO_BODY_MAX_CHARS ? 'hero' : 'chip';
}
